import {useState, useEffect} from "react";
import {useMutation, useQueryClient} from "@tanstack/react-query";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
  DrawerFooter,
} from "@sakura-grp/ui/drawer";
import {Button} from "@sakura-grp/ui/button";
import {Input} from "@sakura-grp/ui/input";
import {Label} from "@sakura-grp/ui/label";
import {toast} from "@sakura-grp/ui/sonner";
import {
  createBusinessCard,
  normalizeMobileInput,
  isValidMobileNumber,
  isValidEmailAddress,
  normalizePostalCodeInput,
  isValidPostalCode,
} from "@/lib/api/business-cards";
import {useDirtyDialogGuard} from "@/hooks/use-dirty-dialog-guard";
import {useAddressAutofill} from "@/hooks/useAddressAutofill";
import {useKeyboardInset} from "@/hooks/useKeyboardInset";
import {useKanaField} from "@/hooks/useKanaField";
import {CompanySelectSheet} from "../shared/company-select-sheet";

interface BusinessCardCreateSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function BusinessCardCreateSheet({open, onOpenChange}: BusinessCardCreateSheetProps) {
  const [name, setName] = useState("");
  const [kana, setKana] = useState("");
  const [mobile, setMobile] = useState("");
  const [email, setEmail] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [address, setAddress] = useState("");
  // 会社名は CompanySelectSheet（一覧から選ぶ / 新規登録）で選ぶ。値は会社名の文字列。
  const [company, setCompany] = useState("");
  const [companySheetOpen, setCompanySheetOpen] = useState(false);
  const queryClient = useQueryClient();

  // 郵便番号7桁で日本郵便 API（/api/postal-code プロキシ）から都道府県〜町域を前置入力
  const {resetAutofill} = useAddressAutofill({postalCode, address, onFill: setAddress});

  // フリガナの検証は IME 変換中のエラー表示抑止込みでフックに委譲。
  // kanaError は確定値の真の検証（canSave 用）、kanaVisibleError は画面表示用。
  const {
    error: kanaError,
    visibleError: kanaVisibleError,
    inputProps: kanaInputProps,
    reset: resetKanaComposition,
  } = useKanaField(kana);

  useEffect(() => {
    if (open) {
      setName("");
      setKana("");
      setMobile("");
      setEmail("");
      setPostalCode("");
      setAddress("");
      setCompany("");
      setCompanySheetOpen(false);
      resetAutofill();
      // 前回 compositionend を取りこぼして変換中フラグが残っていても、開き直しで初期化
      resetKanaComposition();
    }
  }, [open, setCompany, resetAutofill, resetKanaComposition]);

  const mutation = useMutation({
    mutationFn: () =>
      createBusinessCard({
        nameKanji: name,
        nameFurigana: kana,
        mobileNumber: mobile,
        emailAddress: email,
        companyName: company,
        address,
        postalCode,
      }),
    onSuccess: () => {
      // 顧客の作成/リンクは RPC が不変条件として保証（失敗時は error 経路に来る）
      toast.success("名刺を登録しました");
      queryClient.invalidateQueries({queryKey: ["tab-customers"]});
      queryClient.invalidateQueries({queryKey: ["tab-counts"]});
      onOpenChange(false);
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const normalizedMobile = normalizeMobileInput(mobile);
  const mobileError =
    normalizedMobile.length > 0 && !isValidMobileNumber(normalizedMobile)
      ? "携帯電話番号の形式が正しくありません"
      : null;

  const normalizedPostal = normalizePostalCodeInput(postalCode);
  const postalError =
    postalCode.trim().length > 0 && !isValidPostalCode(normalizedPostal)
      ? "郵便番号は7桁の数字で入力してください"
      : null;

  const emailTrimmed = email.trim();
  const emailError =
    emailTrimmed.length > 0 && !isValidEmailAddress(emailTrimmed)
      ? "メールアドレスの形式が正しくありません"
      : null;

  const hasChanges =
    name.trim().length > 0 ||
    kana.trim().length > 0 ||
    mobile.trim().length > 0 ||
    emailTrimmed.length > 0 ||
    postalCode.trim().length > 0 ||
    address.trim().length > 0 ||
    company.trim().length > 0;

  // 入力契約 v3: 必須は氏名・フリガナのみ。連絡先は任意だが、入れるなら正しい形式であること
  const canSave =
    name.trim().length > 0 &&
    kana.trim().length > 0 &&
    !kanaError &&
    !mobileError &&
    !emailError &&
    !postalError;

  const {DirtyConfirmDialog, requestClose} = useDirtyDialogGuard({
    isDirty: hasChanges,
    onClose: () => onOpenChange(false),
  });


  // vaul の repositionInputs は visualViewport.offsetTop を考慮せず、iOS 実機
  // (standalone PWA) でボタンとキーボードの間に offsetTop ぶんの空白を作る
  // (vaul#620)。自前の inset 計算でフッターをキーボード直上に密着させる
  const keyboardInset = useKeyboardInset();

  return (
    <>
      <Drawer
        open={open}
        onOpenChange={onOpenChange}
        dismissible={!hasChanges}
        repositionInputs={false}
      >
        <DrawerContent
          // 高さを 85vh で固定（h-auto だと検証エラーの出現で content が伸び、
          // bottom 固定の drawer が上方向に成長して入力欄が動く）。固定高なら
          // 上端が動かず、増減は内側の overflow-y-auto が吸収するのでガタつかない
          className="px-lg pb-safe-min h-[85vh]"
          style={keyboardInset > 0 ? {paddingBottom: keyboardInset} : undefined}
        >
          <DrawerHeader className="pb-md">
            <DrawerTitle>名刺を登録</DrawerTitle>
            <DrawerDescription>お客様の名刺情報を入力してください</DrawerDescription>
          </DrawerHeader>

          <div className="space-y-md min-h-0 flex-1 overflow-y-auto">
            <div className="space-y-sm">
              <Label htmlFor="card-name">氏名（必須）</Label>
              <Input
                id="card-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="山田 太郎"
                className="text-base"
              />
            </div>
            <div className="space-y-sm">
              <Label htmlFor="card-kana">フリガナ（必須）</Label>
              <Input
                id="card-kana"
                value={kana}
                onChange={(e) => setKana(e.target.value)}
                placeholder="ヤマダ タロウ"
                className="text-base"
                aria-invalid={!!kanaVisibleError}
                {...kanaInputProps}
              />
              {kanaVisibleError && (
                <p className="text-destructive text-xs" role="alert">
                  {kanaVisibleError}
                </p>
              )}
            </div>
            <div className="space-y-sm">
              <Label htmlFor="card-mobile">携帯電話</Label>
              <Input
                id="card-mobile"
                type="tel"
                inputMode="tel"
                value={mobile}
                onChange={(e) => setMobile(e.target.value)}
                placeholder="090-1234-5678"
                className="text-base"
                aria-invalid={!!mobileError}
              />
              {mobileError && (
                <p className="text-destructive text-xs" role="alert">
                  {mobileError}
                </p>
              )}
            </div>
            <div className="space-y-sm">
              <Label htmlFor="card-email">メールアドレス</Label>
              <Input
                id="card-email"
                type="email"
                inputMode="email"
                autoCapitalize="none"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="taro@example.com"
                className="text-base"
                aria-invalid={!!emailError}
              />
              {emailError && (
                <p className="text-destructive text-xs" role="alert">
                  {emailError}
                </p>
              )}
              <p className="text-muted-foreground text-xs">
                携帯電話・メールアドレスは任意です。不明なら空欄のまま登録できます
              </p>
            </div>
            <div className="space-y-sm">
              <Label htmlFor="card-company">会社名</Label>
              <button
                id="card-company"
                type="button"
                onClick={() => setCompanySheetOpen(true)}
                className="border-input bg-input/10 h-11 gap-sm flex w-full items-center justify-between rounded-md border px-3 text-base"
              >
                <span className={company ? "" : "text-muted-foreground/50"}>
                  {company || "会社を選ぶ（任意）"}
                </span>
                <span className="text-muted-foreground shrink-0">›</span>
              </button>
              <p className="text-muted-foreground text-xs">
                一覧から選ぶか、新しい会社を登録できます
              </p>
            </div>
            <div className="space-y-sm">
              <Label htmlFor="card-postal">郵便番号</Label>
              <Input
                id="card-postal"
                inputMode="numeric"
                value={postalCode}
                onChange={(e) => setPostalCode(e.target.value)}
                placeholder="1040061"
                className="text-base"
                aria-invalid={!!postalError}
              />
              {postalError && (
                <p className="text-destructive text-xs" role="alert">
                  {postalError}
                </p>
              )}
            </div>
            <div className="space-y-sm">
              <Label htmlFor="card-address">住所</Label>
              <Input
                id="card-address"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                placeholder="東京都中央区銀座1-2-3 サクラビル5F"
                className="text-base"
              />
            </div>
          </div>

          <DrawerFooter className="p-0 pt-md flex shrink-0 flex-col gap-md">
            <Button
              onClick={() => mutation.mutate()}
              disabled={mutation.isPending || !canSave}
            >
              {mutation.isPending ? "登録中..." : "登録する"}
            </Button>
            <Button
              variant="outline"
              onClick={requestClose}
              disabled={mutation.isPending}
            >
              やめる
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
      <CompanySelectSheet
        open={companySheetOpen}
        onOpenChange={setCompanySheetOpen}
        value={company}
        onSelect={setCompany}
      />
      <DirtyConfirmDialog />
    </>
  );
}
